import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { ISearchControlExposedFunctions, SearchTextControl } from "@/Common/Components/SearchTextControl";
import { useEffectAfterMount } from "@/Common/CustomHooks/useEffectAfterMount";
import { useUserLocation } from "@/Common/CustomHooks/User/useUserLocation";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { SearchResponse } from "@/ContactDirectory/Api/ContactDirectoryClient";
import {
    DirectoryEntityType,
    SearchOrder
} from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectoryPatientLinksHelper } from "@/ContactDirectory/Helpers/DirectoryPatientLinksHelper";
import { useDefaultDirectorySearchResult } from "@/ContactDirectory/Hooks/useDefaultDirectorySearchResult";
import { useDirectoryClientForType } from "@/ContactDirectory/Hooks/useDirectoryClientForType";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models//DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models//DirectoryOtherEstablishment";
import { DirectoryPharmacy } from "@/ContactDirectory/Models//DirectoryPharmacy";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models/DirectoryFaxNumber";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { PatientPreferredDirectoryContactsContext } from "@/ContactDirectory/Models/PatientPreferredDirectoryContactsContext";
import { DirectoryPopupController } from "../DirectoryPopupController";
import { HealthCareProviderInClinicSearchResultItemComponent } from "../EntityComponent/HealthCareProviderInClinicSearchResultItemComponent";
import { PreferredPharmacyComponent } from "../EntityComponent/PreferredPharmacyComponent";
import { DirectorySearchResultItem } from "./DirectorySearchResultItem";
import { FaxDirectorySearchResultItem } from "./FaxDirectorySearchResultItem";

import styles from "./FaxDirectorySearchComponent.module.scss";

interface IFaxDirectorySearchComponentProps {
    entityTypes?: DirectoryEntityType[];
    isValid?: boolean;
    showPreferredPharmacy?: boolean;
    /**
     * required when showing the preferred pharmacy
     */
    patientContactId?: string;
    showExternalConsultants?: boolean;
    showClinicHealthCareProviders?: boolean;
    clearSearchTextOnSelect?: boolean; // true by default
    searchText?: string;
    disableEdit?: boolean;
    componentId?: string;
    maxLength?: number;
    disableSearchField?: boolean;
    linkedFaxNumbers?: DirectoryFaxNumber[];
    /**
     * The current search result.
     * If null or undefined, the search result remains unchanged.
     * Set this prop to an empty array to clear the search result.
    */
    searchResultOverride?: FaxDirectorySearchResultItem[];
    onItemSelected?: (selection: FaxDirectorySearchResultItem) => void;
    setSelectedName?: (name: string) => void;
    onSearchTextChanged?: (name: string, isValid: boolean) => void;
    scrollToSearchResult?: boolean;
    filterExternalConsultants?: boolean;
    dataCy: string;
    placeholder?: string;
    allowExpandingGeographicSearch?: boolean;
}

interface IDirectoryEntityDisplayComponentProps<TItem> {
    onEdit?: (model: TItem) => void;
    list?: TItem[];
    isSubItem?: boolean;
}

const sectionIds = {
    preferredPharmacy: "0",
    externalConsultants: "1",
    otherEstablishment: "2",
    pharmacy: "3",
    healthCareProvider: "4"
};

const preferredIds = [sectionIds.preferredPharmacy, sectionIds.externalConsultants];

const sectionTitleLabelKeys = {
    [sectionIds.preferredPharmacy]: "PreferredPharmacyLabel",
    [sectionIds.externalConsultants]: "ExternalConsultantLabel",
    [sectionIds.otherEstablishment]: "EstablishmentsLabel",
    [sectionIds.pharmacy]: "PharmaciesLabel",
    [sectionIds.healthCareProvider]: "ProfessionalsLabel"
};

function FaxDirectorySearchComponent(props: IFaxDirectorySearchComponentProps): JSX.Element {
    const { getText } = useTranslation();
    const [editModel, setEditModel] = useState<IDirectoryEntity | null>(null);
    const [searchAllCountriesAndStates, setSearchAllCountriesAndStates] = useState<boolean>(false);
    const client = useDirectoryClientForType(null);
    const clinicProviders = useMemo(() => cacheStore.GetClinicianDoctors().map(d => DirectoryHealthCareProvider.fromPerson(d)), []);

    const { preferredPharmacy, externalConsultants } = useContext(PatientPreferredDirectoryContactsContext);

    const defaultSearchResult = useDefaultDirectorySearchResult({
        preferredPharmacy: props.showPreferredPharmacy ? preferredPharmacy : null,
        externalConsultants: props.showExternalConsultants ? externalConsultants : null,
        clinicProviders: props.showClinicHealthCareProviders ? clinicProviders : null
    });
    const [searchResult, setSearchResult] = useState<FaxDirectorySearchResultItem[]>(props.searchResultOverride);
    const clearSearchTextOnSelect = useMemo(() => props.clearSearchTextOnSelect ?? true, [props.clearSearchTextOnSelect]);

    useEffectAfterMount(() => searchRef.current.triggerSearch(), [searchAllCountriesAndStates]);

    useEffect(() => props.searchResultOverride && setSearchResult(props.searchResultOverride), [props.searchResultOverride]);

    const { geoCountry, geoState } = useUserLocation();

    async function onSearch(searchText: string): Promise<FaxDirectorySearchResultItem[]> {
        const list: FaxDirectorySearchResultItem[] = [];

        let data: SearchResponse;
        try {
            data = await client.search(searchText, props.entityTypes ?? null, geoCountry?.Id, geoState?.Id, !searchAllCountriesAndStates);
        }
        catch (e) {
            console.warn(e);
            return [];
        }

        let orderDict: { [key: string]: SearchOrder } = {};
        if (data.searchOrder?.any())
            orderDict = data.searchOrder?.toDictionary(p => `${p.entityType}-${p.id}`);

        if (props.entityTypes?.contains(DirectoryEntityType.HealthcareProvider) || !props.entityTypes?.any()) {
            const HealthcareProvider = (data.healthCareProviders ?? [])?.filter(p => !props.linkedFaxNumbers?.contains(p.faxNumber));
            list.push(...HealthcareProvider.map(p => new FaxDirectorySearchResultItem({
                entity: DirectoryHealthCareProvider.fromSearchDto(p),
                orderNumber: orderDict[`${DirectoryEntityType.HealthcareProvider}-${p.id}`]?.order
            })));
        }

        if (props.entityTypes?.contains(DirectoryEntityType.Other) || !props.entityTypes?.any()) {
            const otherEstablishments = (data.otherEstablishments ?? [])?.filter(p => p.faxNumbers?.any(p => !props.linkedFaxNumbers?.any(x => x.number === p.number)));
            list.push(...otherEstablishments.map(p => new FaxDirectorySearchResultItem({
                entity: DirectoryOtherEstablishment.fromSearchDto(p),
                orderNumber: orderDict[`${DirectoryEntityType.Other}-${p.id}`]?.order,
                selectedFaxNumberId: p.faxNumbers?.firstOrDefault(fax => p.matchingFaxNumberIds?.includes(fax.id))?.id
            })));
        }

        if (props.entityTypes?.contains(DirectoryEntityType.Pharmacy) || !props.entityTypes?.any()) {
            const pharmacies = (data.pharmacies ?? [])?.filter(p => !props.linkedFaxNumbers?.contains(p.faxNumber));
            list.push(...pharmacies.map(p => new FaxDirectorySearchResultItem({
                entity: DirectoryPharmacy.fromSearchDto(p),
                orderNumber: orderDict[`${DirectoryEntityType.Pharmacy}-${p.id}`]?.order
            })));
        }

        const sortedList = list.sortByProperty(p => p.orderNumber ?? 0);
        setSearchResult(sortedList);

        return sortedList;
    }

    const searchRef = useRef<ISearchControlExposedFunctions<FaxDirectorySearchResultItem>>(null);

    function isHealthCareProviderLinkedToCurrentPatient(entity: IDirectoryEntity): boolean {
        const patientContactId = patientStore.getCurrentPatientContactId();
        const externalConsultantList = DirectoryPatientLinksHelper.getExternalConsultants(patientContactId);

        return (entity instanceof DirectoryHealthCareProvider && externalConsultantList?.some(p => p.id === entity.id));
    }

    return (
        <div>
            {editModel && (
                <DirectoryPopupController
                    onModify={entity => {
                        props.onItemSelected?.(new FaxDirectorySearchResultItem({ entity }));
                        if (searchResult?.any()) {
                            setSearchResult(searchResult.map(
                                item => item.entity && item.entity.constructor === entity.constructor && item.entity.id === entity.id
                                    ? new FaxDirectorySearchResultItem({ ...item, entity }) // update list with modified entity
                                    : item
                            ));
                        }
                        setEditModel(null);

                        if (clearSearchTextOnSelect)
                            searchRef?.current?.resetSearch?.();
                    }}
                    onFlagAsInvalid={entity => setSearchResult(searchResult?.filter(p => !(p.entity && p.entity.constructor === entity.constructor && p.entity.id === entity.id)))}
                    onClose={() => setEditModel(null)}
                    model={editModel}
                    editModel={editModel}
                    onEntityUseInstead={entity => {
                        props.onItemSelected?.(new FaxDirectorySearchResultItem({ entity }));
                        setEditModel(null);
                    }}
                />
            )}

            <SearchTextControl
                id={props.componentId}
                ref={searchRef}
                dataCy={props.dataCy}
                searchText={props.searchText}
                defaultSearchResult={defaultSearchResult}
                searchResultOverride={searchResult}
                onTextChange={text => props.onSearchTextChanged?.(text, !IsNullOrWhiteSpace(text))}
                onSearch={onSearch}
                renderItem={item => {
                    if (!item)
                        return null;

                    if (item.preferredPharmacy)
                        return (
                            <PreferredPharmacyComponent
                                preferredPharmacy={item.preferredPharmacy}
                                patientContactId={props.patientContactId}
                                onEdit={entity => setEditModel(entity)}
                                disableEdit={props.disableEdit}
                            />
                        );

                    if (item.entity instanceof DirectoryHealthCareProvider
                            && props.showClinicHealthCareProviders
                            && IsNullOrWhiteSpace(props.searchText))
                        return (
                            <HealthCareProviderInClinicSearchResultItemComponent
                                healthCareProvider={item.entity}
                                showContacts={false}
                            />
                        );

                    if (!props.filterExternalConsultants || !isHealthCareProviderLinkedToCurrentPatient(item.entity))
                        return (
                            <DirectorySearchResultItem
                                item={item}
                                onEdit={entity => setEditModel(entity)}
                                disableEdit={props.disableEdit}
                                selectFaxNumber={faxNumberId => {
                                    const newItem = new FaxDirectorySearchResultItem({ ...item, selectedFaxNumberId: faxNumberId });
                                    searchRef.current.customSelectItem(newItem);
                                }}
                            />
                        );
                }}
                getItemId={item => item.id}
                onItemSelected={item => props.onItemSelected(item)}
                renderSection={(sectionId, children) => {
                    if (!children?.any())
                        return null;

                    if (preferredIds.contains(sectionId)) {
                        return (
                            <div className={styles.subContainer}>
                                <h4>{getText(sectionTitleLabelKeys[sectionId])}</h4>
                                {children}
                            </div>
                        );
                    }
                    else {
                        return (
                            <div className={styles.subContainerSection}>
                                <h4>{getText(sectionTitleLabelKeys[sectionId])}</h4>
                                {children}
                            </div>
                        );
                    }
                }}
                getSectionId={item => {
                    if (item?.preferredPharmacy)
                        return sectionIds.preferredPharmacy;

                    else if (item?.entity instanceof DirectoryHealthCareProvider && externalConsultants.map(p => p.id).contains(item.entity.id))
                        return sectionIds.externalConsultants;

                    else if (item?.entity?.type === DirectoryEntityType.Other)
                        return sectionIds.otherEstablishment;

                    else if (item?.entity?.type === DirectoryEntityType.Pharmacy)
                        return sectionIds.pharmacy;

                    if (item?.entity?.type === DirectoryEntityType.HealthcareProvider)
                        return sectionIds.healthCareProvider;

                    return "5";
                }}
                isValid={props.isValid || props.searchText?.length >= 3}
                clearSearchTextOnSelect={clearSearchTextOnSelect}
                disableSearchField={props.disableSearchField}
                scrollToSearchResult={props.scrollToSearchResult}
                maxLength={250}
                placeholder={props.placeholder}
            />
            {props.allowExpandingGeographicSearch && (
                <CheckboxControl
                    checked={searchAllCountriesAndStates}
                    onChange={checked => setSearchAllCountriesAndStates(checked)}
                    label={getText("SearchAllCountriesAndStatesLabel")}
                />
            )}
        </div>
    );
}

export { FaxDirectorySearchComponent, IDirectoryEntityDisplayComponentProps };
